import { usePage } from "@inertiajs/react"
import { useState } from "react"

// Signing in starts a new session without reloading the page, so the meta tag can be stale; the XSRF-TOKEN cookie is fresh.
export const csrfToken = () => {
  const cookie = document.cookie.split("; ").find((part) => part.startsWith("XSRF-TOKEN="))
  return cookie ? decodeURIComponent(cookie.slice("XSRF-TOKEN=".length)) : (document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "")
}

// Tells the server a share happened (for the share-rate stat) without waiting on it.
const record = (entryId: number) => {
  void fetch(`/entries/${entryId}/share`, { method: "POST", headers: { "X-CSRF-Token": csrfToken() } }).catch(() => undefined)
}

export type ShareKind = "result" | "clean_game" | "champion" | "monthly_champion" | "live_invite" | "league_invite" | "hand_spot"
type ShareOutcome = "shared" | "cancelled" | "failed" | "copied" | "shown" | "saved"

// Records a share tap for the admin's Shares page: which button and how it ended. Never waits or gets in the way.
export const trackShare = (kind: ShareKind, outcome: ShareOutcome) => {
  void fetch("/share-events", {
    method: "POST",
    keepalive: true,
    headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
    body: JSON.stringify({ kind, outcome, path: window.location.pathname }),
  }).catch(() => undefined)
}

// Opens the phone's share sheet and says how it ended: sent, closed by the player, or refused by the browser.
export const shareSheet = (data: ShareData): Promise<ShareOutcome> =>
  navigator.share(data).then(
    () => "shared",
    (error: unknown) => (error instanceof DOMException && error.name === "AbortError" ? "cancelled" : "failed"),
  )

// The phone's share sheet where there is one, otherwise copy to the clipboard. Some in-app
// browsers block both, so the text is shown, selected, to copy by hand. entryId: a run's result,
// counted for the share-rate stat. url: a link shared beside the text.
export default function ShareButton({
  text,
  kind = "result",
  entryId,
  url,
  label = "Share result",
  className = "",
  outline = false,
}: {
  text: string
  kind?: ShareKind
  entryId?: number
  url?: string
  label?: string
  className?: string
  outline?: boolean
}) {
  const [state, setState] = useState<"idle" | "copied" | "manual">("idle")
  const full = url ? `${text} ${url}` : text
  const { viewingAs } = usePage().props

  const share = async () => {
    if (entryId && !viewingAs) record(entryId)
    const track = (outcome: ShareOutcome) => !viewingAs && trackShare(kind, outcome)
    if (navigator.share !== undefined) {
      track(await shareSheet(url ? { text, url } : { text }))
      return
    }
    try {
      await navigator.clipboard.writeText(full)
      setState("copied")
      track("copied")
      window.setTimeout(() => setState("idle"), 2000)
    } catch {
      setState("manual")
      track("shown")
    }
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={share}
        className={
          outline
            ? "min-h-12 w-full rounded-lg border border-brass px-3 font-condensed text-lg font-semibold text-brass focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass"
            : "w-full rounded-lg bg-brass px-4 py-2.5 font-condensed text-lg font-semibold text-rail focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
        }
      >
        {state === "copied" ? "Copied to clipboard" : label}
      </button>
      {state === "manual" && (
        <label className="mt-2 block text-sm text-ink-muted">
          Copy this and paste it in your group chat:
          <textarea
            readOnly
            autoFocus
            rows={4}
            value={full}
            onFocus={(event) => event.currentTarget.select()}
            className="mt-1 block w-full rounded-md border border-white/15 bg-rail p-2 font-mono text-sm text-ink"
          />
        </label>
      )}
    </div>
  )
}
